// Invite-only sign-up - the front door. A valid invite code admits the parent
// to account setup and is carried forward to Create Account.

import { useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'

import { Grain } from '@/components/hs/Grain'
import { WelcomeCourt } from '@/components/hs/WelcomeCourt'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { capture } from '@/lib/analytics'
import { INVITE_CODE_KEY, stashInviteCode } from '@/lib/inviteCode'
import '@/styles/auth-pages.css'

const IDENTITY_BASE = import.meta.env.VITE_API_URL_IDENTITY as string

export { INVITE_CODE_KEY }

const ARROW = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 10h13M11 4l6 6-6 6" />
  </svg>
)

function SignupBox({ active, ch, compact }: { active: boolean; ch: string; compact: boolean }) {
  return (
    <div className={`auth-signup-box${compact ? ' is-compact' : ''}${ch ? ' is-filled' : ''}${active ? ' is-active' : ''}`}>
      {ch || (active ? <span className="auth-signup-box__cursor" /> : '')}
    </div>
  )
}

export function InviteTicket({
  code,
  compact = false,
  setCode,
}: {
  code: string
  compact?: boolean
  setCode: (c: string) => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  const slots = Array.from({ length: 6 }, (_, i) => code[i] || '')

  return (
    <div className={`auth-ticket${compact ? ' is-compact' : ''}`} onClick={() => ref.current?.focus()}>
      <div className="auth-ticket__shadow" />
      <div className="auth-ticket__back" />

      <div className="auth-ticket__body">
        <div className="auth-ticket__main">
          <div className="auth-eyebrow auth-ticket__label">Your invite code</div>
          <div className="auth-ticket__slots">
            {slots.slice(0, 3).map((c, i) => (
              <SignupBox key={i} ch={c} active={i === code.length} compact={compact} />
            ))}
            <span className="auth-ticket__divider" />
            {slots.slice(3, 6).map((c, i) => (
              <SignupBox key={i + 3} ch={c} active={i + 3 === code.length} compact={compact} />
            ))}
          </div>
          <input
            ref={ref}
            value={code}
            inputMode="text"
            autoComplete="off"
            aria-label="Invite code"
            autoFocus
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
            className="auth-ticket__input"
          />
        </div>

        <div className="auth-ticket__stub">
          <div className="auth-ticket__notch auth-ticket__notch--top" />
          <div className="auth-ticket__notch auth-ticket__notch--bottom" />
          <div className="auth-ticket__stub-copy">
            <div className="auth-ticket__stub-title">ADMIT ONE</div>
            <div className="auth-ticket__stub-subtitle">family</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function SignUpPage() {
  const { isAuthenticated, isLoading } = useAuth0()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [code, setCode] = useState('')
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [waitlistNote, setWaitlistNote] = useState(false)

  const ready = code.length === 6

  if (isAuthenticated && !isLoading) {
    return <Navigate to="/" replace />
  }

  const stepInside = async () => {
    if (!ready || checking) return
    setChecking(true)
    setError(null)

    try {
      const res = await fetch(`${IDENTITY_BASE}/api/invites/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      })
      if (!res.ok) throw new Error(String(res.status))

      const { exists, used } = (await res.json()) as { exists: boolean; used?: boolean }
      capture('invite_code_checked', { valid: exists && !used, used: !!used })

      if (exists && !used) {
        stashInviteCode(code)
        navigate('/auth/create-account', { state: { code } })
      } else if (exists && used) {
        setError('That code has already been used. Ask us for a fresh one.')
      } else {
        setError("We don't recognize that code. Check it and try again.")
      }
    } catch {
      setError('Could not check that code right now. Please try again.')
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="hs-brand auth-page auth-signup">
      <Grain opacity={0.06} />

      <div className="auth-page__topbar auth-page__topbar--split">
        <div className="auth-brand-lockup">
          <span className="auth-brand-mark" />
          <span className="auth-brand-name">Hopscotch</span>
        </div>
        <div className="auth-returning">
          {isMobile ? null : 'Already have an account? '}
          <button type="button" className="auth-link-button" onClick={() => navigate('/auth/login')}>
            Sign in
          </button>
        </div>
      </div>

      <div className="auth-page__spread auth-page__spread--signup">
        <div className="auth-page__spread-left auth-page__spread-left--signup">
          <div className="auth-eyebrow auth-page__eyebrow">Invite only - for now</div>
          <h1 className="auth-heading auth-heading--signup">
            Come on{' '}
            <span className="auth-highlight">
              <span className="auth-highlight__mark auth-highlight__mark--signup" />
              <span className="auth-highlight__text">in.</span>
            </span>
          </h1>
          <p className="auth-copy auth-copy--signup">
            Hopscotch is invite-only for now, so every family gets our full attention. Enter the code you were sent and we&rsquo;ll set up your account.
          </p>

          <InviteTicket code={code} compact={isMobile} setCode={(c) => { setCode(c); if (error) setError(null) }} />

          {error ? <p className="auth-error auth-error--signup">{error}</p> : null}

          <div className={`auth-action-row auth-action-row--signup${error ? ' has-error' : ''}`}>
            <button
              type="button"
              onClick={stepInside}
              disabled={!ready || checking}
              className={`auth-submit-button${ready ? ' hs-press' : ' is-disabled'}`}
            >
              {checking ? 'Checking...' : 'Step inside'}
              {!checking && ARROW}
            </button>
            <span className="auth-waitlist">
              {waitlistNote ? (
                'Waitlist is coming soon.'
              ) : (
                <>
                  No code yet?{' '}
                  <button type="button" className="auth-link-button" onClick={() => setWaitlistNote(true)}>
                    Join the waitlist
                  </button>
                </>
              )}
            </span>
          </div>
        </div>

        <div className="auth-page__spread-right auth-signup__court">
          <WelcomeCourt maxWidth={isMobile ? 220 : 470} />
        </div>
      </div>
    </div>
  )
}
